/**
 * Profile Modal Component
 * 
 * 내 정보 모달 - 프로필 정보 편집 및 회원 탈퇴
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const AUTH_TOKEN_KEY = 'auth_token';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { t } = useLanguage();
  const { user, updateProfile, refreshUser } = useAuth();
  
  // 폼 상태
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [domain, setDomain] = useState('');
  const [position, setPosition] = useState('');
  
  // 원본 값 (변경 감지용)
  const [originalValues, setOriginalValues] = useState({
    name: '',
    phone: '',
    company: '',
    domain: '',
    position: '',
  });
  
  // UI 상태
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // 프로필 이미지 상태
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  
  
  // 사용자 정보로 폼 초기화
  useEffect(() => {
    if (user && isOpen) {
      const values = {
        name: user.name || '',
        phone: user.phone || '',
        company: user.company || '',
        domain: user.domain || '',
        position: user.position || '',
      };
      setName(values.name);
      setPhone(values.phone);
      setCompany(values.company);
      setDomain(values.domain);
      setPosition(values.position);
      setOriginalValues(values);
      setError(null);
      setSuccess(false);
    }
  }, [user, isOpen]);
  
  // 변경사항 감지
  const hasChanges = 
    name !== originalValues.name ||
    phone !== originalValues.phone ||
    company !== originalValues.company ||
    domain !== originalValues.domain ||
    position !== originalValues.position;
  
  if (!isOpen || !user) return null;
  
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      await updateProfile({
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        company: company.trim() || undefined,
        domain: domain.trim() || undefined,
        position: position.trim() || undefined,
      });
      // 원본 값 업데이트 (변경사항 초기화)
      setOriginalValues({
        name: name.trim(),
        phone: phone.trim(),
        company: company.trim(),
        domain: domain.trim(),
        position: position.trim(),
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.profile.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 프로필 이미지 업로드
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t.profile.imageTypeError || '지원하지 않는 파일 형식입니다. (JPEG, PNG, GIF, WebP만 가능)');
      return;
    }

    // 파일 크기 검증 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t.profile.imageSizeError || '파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    setIsUploadingImage(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/api/user/profile-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        // 503 Service Unavailable - 스토리지 미설정
        if (res.status === 503) {
          throw new Error(t.profile.storageNotAvailable || '로컬 환경에서는 이미지 업로드가 지원되지 않습니다.');
        }
        throw new Error(payload?.detail || t.profile.imageUploadFailed || '이미지 업로드에 실패했습니다.');
      }

      await refreshUser();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.profile.imageUploadFailed || '이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingImage(false);
      // input 초기화
      e.target.value = '';
    }
  };

  // 프로필 이미지 삭제
  const handleImageDelete = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    setIsDeletingImage(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/user/profile-image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.detail || t.profile.imageDeleteFailed || '이미지 삭제에 실패했습니다.');
      }

      await refreshUser();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.profile.imageDeleteFailed || '이미지 삭제에 실패했습니다.');
    } finally {
      setIsDeletingImage(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-terminal-border">
          <h2 className="text-lg font-semibold text-terminal-text">
            {t.profile.title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-terminal-muted hover:text-terminal-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* 컨텐츠 */}
        <div className="p-6 space-y-5">
          {/* 에러/성공 메시지 */}
          {error && (
            <div className="p-3 bg-status-nogo/10 border border-status-nogo/30 rounded-lg text-status-nogo text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-status-go/10 border border-status-go/30 rounded-lg text-status-go text-sm">
              {t.profile.saveSuccess}
            </div>
          )}

          {/* 프로필 이미지 */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || 'Profile'}
                  className="w-20 h-20 rounded-full object-cover border-2 border-terminal-border"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-terminal-dim/30 border-2 border-terminal-border flex items-center justify-center">
                  <span className="text-3xl text-terminal-muted">
                    {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
              {(isUploadingImage || isDeletingImage) && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-terminal-muted">{t.profile.profileImage || '프로필 이미지'}</label>
              <div className="flex items-center gap-2">
                <label className="px-3 py-1.5 bg-terminal-dim/20 hover:bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-text text-xs font-medium transition-colors cursor-pointer disabled:opacity-50">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    disabled={isUploadingImage || isDeletingImage}
                    className="hidden"
                  />
                  {isUploadingImage ? (t.profile.uploading || '업로드 중...') : (user.picture ? (t.profile.changeImage || '변경') : (t.profile.uploadImage || '업로드'))}
                </label>
                {user.picture && (
                  <button
                    onClick={handleImageDelete}
                    disabled={isUploadingImage || isDeletingImage}
                    className="px-3 py-1.5 bg-status-nogo/10 hover:bg-status-nogo/20 border border-status-nogo/30 rounded-lg text-status-nogo text-xs font-medium transition-colors disabled:opacity-50"
                  >
                    {isDeletingImage ? (t.profile.deleting || '삭제 중...') : (t.profile.deleteImage || '삭제')}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-terminal-muted">
                {t.profile.imageHint || 'JPEG, PNG, GIF, WebP (최대 5MB)'}
              </p>
            </div>
          </div>
          
          {/* 이메일 __________ (읽기 전용) */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <label className="text-xs text-terminal-muted sm:w-24 flex-shrink-0">
              {t.profile.email}
            </label>
            <div className="flex-1 px-3 py-2.5 bg-terminal-dim/30 border border-terminal-border rounded-lg text-terminal-muted text-sm cursor-not-allowed">
              {user.email}
            </div>
          </div>

          {/* 이름 __________   휴대폰 번호 __________ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-terminal-muted mb-1.5">
                {t.profile.name}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.profile.namePlaceholder}
                className="w-full px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs text-terminal-muted mb-1.5">
                {t.profile.phone}
              </label>
              <div className="flex gap-2">
                <select
                  value={phone.startsWith('+') ? phone.split(' ')[0] : '+82'}
                  onChange={(e) => {
                    const countryCode = e.target.value;
                    const currentNumber = phone.includes(' ')
                      ? phone.split(' ').slice(1).join(' ')
                      : (phone.startsWith('+') ? '' : phone);
                    const normalized = currentNumber.replace(/[^\d-]/g, '');
                    setPhone(normalized ? `${countryCode} ${normalized}` : countryCode);
                  }}
                  className="w-28 px-2 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm focus:outline-none focus:border-status-go/50 transition-colors cursor-pointer"
                >
                  <option value="+82">🇰🇷 +82</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+86">🇨🇳 +86</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+65">🇸🇬 +65</option>
                  <option value="+852">🇭🇰 +852</option>
                  <option value="+84">🇻🇳 +84</option>
                  <option value="+66">🇹🇭 +66</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+91">🇮🇳 +91</option>
                </select>
                <input
                  type="tel"
                  value={phone.includes(' ')
                    ? phone.split(' ').slice(1).join(' ')
                    : (phone.startsWith('+') ? '' : phone)}
                  onChange={(e) => {
                    const countryCode = phone.startsWith('+') ? phone.split(' ')[0] : '+82';
                    const number = e.target.value.replace(/[^\d-]/g, '');
                    setPhone(number ? `${countryCode} ${number}` : countryCode);
                  }}
                  placeholder={t.profile.phonePlaceholder}
                  className="flex-1 px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* 회사 소속 / 도메인 / 포지션 */}
          <div className="space-y-2">
            <div className="hidden md:grid md:grid-cols-3 gap-4">
              <span className="text-xs text-terminal-muted">{t.profile.company}</span>
              <span className="text-xs text-terminal-muted">{t.profile.domain}</span>
              <span className="text-xs text-terminal-muted">{t.profile.position}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:hidden">
                <label className="block text-xs text-terminal-muted mb-1.5">{t.profile.company}</label>
              </div>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={t.profile.companyPlaceholder}
                className="w-full px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors md:col-span-1"
              />

              <div className="md:hidden">
                <label className="block text-xs text-terminal-muted mb-1.5">{t.profile.domain}</label>
              </div>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder={t.profile.domainPlaceholder}
                className="w-full px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors md:col-span-1"
              />

              <div className="md:hidden">
                <label className="block text-xs text-terminal-muted mb-1.5">{t.profile.position}</label>
              </div>
              <input
                type="text"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder={t.profile.positionPlaceholder}
                className="w-full px-3 py-2.5 bg-terminal-dim/20 border border-terminal-border rounded-lg text-terminal-text text-sm placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50 transition-colors md:col-span-1"
              />
            </div>
          </div>

          {/* 하단 액션: [저장] 중앙 */}
          <div className="pt-4 mt-2 border-t border-terminal-border flex justify-center">
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className={`px-10 py-2.5 border rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                hasChanges 
                  ? 'bg-status-go/20 hover:bg-status-go/30 border-status-go/50 text-status-go' 
                  : 'bg-terminal-dim/20 border-terminal-border text-terminal-muted opacity-50'
              }`}
            >
              {isSaving ? t.profile.saving : t.profile.save}
            </button>
          </div>
        </div>
      </div>
      
      </div>
  );
}

